import Alert from "@components/Alert";
import { appendCid, mintCid } from "@libs/rudderstack/cid";
import { CustomActionLink, DirectLinkData, ShareOptions } from "types/branch";
import { showShareDialog } from "./shareDialog.web";
import { getSessionAttribution, track } from "@utils/rudderstack";
import config from "@utils/config";
import sdk from "@farcaster/miniapp-sdk";
import { toast } from "sonner-native";
import { buildReferFriendPath } from "./referral";
import { buildShareUrl } from "./shareUrl";

/**
 * Build a public share-page URL (served by the API) that includes `fc:miniapp`
 * meta tags for Farcaster feed embeds.
 */
const getFarcasterSharePageUrl = (data: DirectLinkData): string | null => {
  const apiBase = (config.API_URL || "").replace(/\/$/, "");
  if (!apiBase) return null;

  if (data.contentType === "song") {
    return `${apiBase}/api/rest/share/song/${data.data.id}`;
  }

  if (data.contentType === "album") {
    return `${apiBase}/api/rest/share/album/${data.data.id}`;
  }

  return null;
};

const copyToClipboard = async (url: string): Promise<boolean> => {
  if (!navigator?.clipboard?.writeText) {
    console.warn("::shareLink - clipboard API not available");
    return false;
  }

  await navigator.clipboard.writeText(url);
  return true;
};

/**
 * Present the share dialog for a Lissen link.
 *
 * The URL is known up front — no link minting, no network round-trip. Shares
 * point at open.lissen.com, which opens the installed app natively as a
 * universal link, serves per-content Open Graph tags to crawlers, and 302s
 * everyone else to the web app.
 */
const shareLink = async ({
  title,
  description,
  url,
}: {
  title: string;
  description: string;
  url: string;
}) => {
  try {
    // Mint the click/share id once per share dialog (LIS-110): whichever way
    // the user shares, the same cid rides the URL and the event.
    const cid = mintCid();
    const shareUrl = appendCid(url, cid);
    const trackShare = () =>
      track("share_link_created", {
        link: shareUrl,
        cid,
        // Viral chains: the cid that started the sharer's own session.
        parent_cid: getSessionAttribution().cid,
      });

    await showShareDialog({
      onCopyLink: async () => {
        try {
          if (await copyToClipboard(shareUrl)) {
            toast.success("Copied to clipboard!", { duration: 1500 });
            trackShare();
          }
        } catch (error) {
          console.warn("::shareLink - copy failed", error);
        }
      },
      onShareVia: async () => {
        if (navigator?.share) {
          try {
            await navigator.share({ text: description, title, url: shareUrl });
            trackShare();
          } catch (error) {
            console.error("::shareLink error sharing", error);
          }
          return;
        }

        console.warn("Web Share API unavailable – falling back to copy");
        if (await copyToClipboard(shareUrl)) {
          Alert.alert("Link copied to clipboard");
          trackShare();
          return;
        }

        Alert.alert("Share this link", shareUrl);
      },
    });
  } catch (error) {
    console.error("::shareLink error sharing", error);
  }
};

const createDirectLink = async ({ contentType, data }: DirectLinkData) => {
  let title = `Check out this ${contentType} on Lissen!`;

  let description = "";
  let path = "";

  if (contentType === "song") {
    let collectionType = "album";
    let collectionId = data.albumId;

    if (data.collectionType === "playlist" && data.collectionId) {
      collectionType = data.collectionType;
      collectionId = data.collectionId;
    }

    description = `${data.title} by ${data.artistDisplayText}`;
    if (data.collectionType === "foryou") {
      path = `/(app)/(tabs)/home?collectionType=album&collectionId=${data.albumId}&songId=${data.id}&shareSource=web-share&shareVersion=1`;
    } else if (data.playlistVisibility === "private") {
      path = `/(app)/(tabs)/explore/album/${data.albumId}/song/${data.id}`;
    } else {
      path = `/(app)/(tabs)/explore/${collectionType}/${collectionId}/song/${data.id}`;
    }
  }

  if (contentType === "access") {
    description = `${data.title}`;
    path = `/access/${data.accessId}`;
  }

  if (contentType === "album") {
    description = `${data.title} by ${data.artistDisplayText}`;
    path = `/(app)/(tabs)/explore/${contentType}/${data.id}`;
  }

  if (contentType === "playlist") {
    description = `${data.title} created by ${data.user?.name || "Lissen"}`;
    path = `/(app)/(tabs)/explore/${contentType}/${data.id}`;
  }

  if (contentType === "artist") {
    description = `${data.name}`;
    path = `/(app)/(tabs)/explore/${contentType}/${data.id}`;
  }

  if (contentType === "challenge") {
    // we want to display different title if the artist is sharing the challenge
    title = data.isArtistSharing
      ? `Challenge by ${data.artistName}`
      : "Check out this experience on Lissen!";
    path = `/(app)/(tabs)/explore/artist/${data.artistId}/experiences?experienceId=${data.experienceId}`;
  }

  if (contentType === "gift") {
    // we want to display different title if the artist is sharing the gift
    title = data.isArtistSharing
      ? `${data.artistName} offered you a gift on Lissen!`
      : "Check out this experience on Lissen!";
    path = `/(app)/(tabs)/explore/experience/${data.experienceId}?giftId=${data.giftId}`;
  }

  // Farcaster Mini App: share a direct, embeddable URL (served by the API)
  // so Farcaster can scrape dynamic artwork + button title.
  const sharePageUrl = getFarcasterSharePageUrl({
    contentType,
    data,
  } as DirectLinkData);
  if (sharePageUrl) {
    const isInMiniApp = await sdk.isInMiniApp().catch(() => false);
    if (isInMiniApp) {
      await showShareDialog({
        onCopyLink: async () => {
          if (await copyToClipboard(sharePageUrl)) {
            toast.success("Copied to clipboard!", { duration: 1500 });
          } else {
            Alert.alert("Share this link", sharePageUrl);
          }
        },
        onShareVia: async () => {
          if (navigator?.share) {
            await navigator.share({
              text: description,
              title,
              url: sharePageUrl,
            });
          } else if (await copyToClipboard(sharePageUrl)) {
            Alert.alert("Link copied to clipboard");
          } else {
            Alert.alert("Share this link", sharePageUrl);
          }
        },
        onShareInFeed: async () => {
          try {
            await sdk.actions.composeCast({
              text: description,
              embeds: [sharePageUrl],
            });
          } catch (error) {
            console.error("::share - composeCast failed", error);
            // Fallback: at least copy the link
            if (await copyToClipboard(sharePageUrl)) {
              toast.success("Copied to clipboard!", { duration: 1500 });
            }
          }
        },
      });

      return;
    }
  }

  return shareLink({ title, description, url: buildShareUrl(path) });
};

const createCustomActionLink = async (data: CustomActionLink) => {
  // The action rides the URL itself, so the recipient's app (or the web app)
  // can act on it without any link-attribution SDK in the middle.
  const path = data.userId ? buildReferFriendPath(data.userId) : "/";

  return shareLink({
    title: `Check out Lissen!`,
    description: "",
    url: buildShareUrl(path),
  });
};

/**
 * Share a link to a specific page on Lissen. Makes it simple to call from the UI.
 */
export const share = async ({ linkType, ...rest }: ShareOptions) => {
  switch (linkType) {
    case "direct-link":
      return createDirectLink(rest as DirectLinkData);
    case "custom-action-link":
      return createCustomActionLink(rest as CustomActionLink);
    default:
      throw new Error("Invalid share linkType");
  }
};
